import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import heroVilla1 from "@/assets/hero-villa-1.png?format=webp&w=1920&quality=90";
import heroVilla1Srcset from "@/assets/hero-villa-1.png?format=webp&w=640;960;1280;1600;1920&quality=90&as=srcset";
import heroVilla2 from "@/assets/hero-villa-2.png?format=webp&w=1920&quality=90";
import heroVilla2Srcset from "@/assets/hero-villa-2.png?format=webp&w=640;960;1280;1600;1920&quality=90&as=srcset";
import heroVillaPool from "@/assets/hero-villa-pool.png?format=webp&w=1920&quality=90";
import heroVillaPoolSrcset from "@/assets/hero-villa-pool.png?format=webp&w=640;960;1280;1600;1920&quality=90&as=srcset";

const Hero = () => {
  const { t } = useTranslation();
  const heroes = useMemo(
    () => [
      { src: heroVilla1, srcset: heroVilla1Srcset },
      { src: heroVilla2, srcset: heroVilla2Srcset },
      { src: heroVillaPool, srcset: heroVillaPoolSrcset },
    ],
    []
  );
  const startSeedRef = useRef<number>(Math.floor(Math.random() * 3));
  const [index, setIndex] = useState(() => startSeedRef.current);
  const [mountRest, setMountRest] = useState(false);
  const [ratios, setRatios] = useState<Record<number, number>>({});
  useEffect(() => {
    // Defer mounting the non-visible heroes so the first paint is fast
    const w = window as any;
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(() => setMountRest(true), { timeout: 2500 });
    } else {
      window.setTimeout(() => setMountRest(true), 1500);
    }
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % heroes.length);
    }, 20000);
    return () => window.clearInterval(id);
  }, [heroes.length]);

  return (
    <section className="relative w-full bg-background" style={{ paddingTop: "var(--nav-h)" }}>
      <div
        className="relative w-full bg-background"
        style={{ aspectRatio: String(ratios[index] ?? 3 / 2) }}
      >
        {heroes.map((hero, i) => (i !== startSeedRef.current && !mountRest ? null : (
          <img
            key={hero.src}
            src={hero.src}
            srcSet={hero.srcset}
            sizes="(max-width: 768px) 100vw, 100vw"
            alt="Premium villa in Hua Hin Thailand with infinity pool"
            fetchPriority={i === startSeedRef.current ? "high" : "low"}
            loading={i === startSeedRef.current ? "eager" : "lazy"}
            decoding={i === startSeedRef.current ? "sync" : "async"}
            width={1600}
            height={1067}
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth && img.naturalHeight) {
                const r = img.naturalWidth / img.naturalHeight;
                setRatios((prev) => (prev[i] === r ? prev : { ...prev, [i]: r }));
              }
            }}
            className={`absolute inset-0 w-full h-full object-contain object-top transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
          />
        )))}
        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 container mx-auto px-6 lg:px-12 z-10 pb-4 md:pb-6">
        <div className="flex justify-center animate-fade-in">
          <Button 
            size="lg" 
            variant="outline"
            className="h-14 px-14 text-sm font-bold tracking-ultra uppercase border-2 border-primary-foreground/60 bg-white/40 text-primary-foreground hover:bg-primary-foreground/25 hover:border-primary-foreground transition-elegant"
            asChild
          >
            <a href="#layouts">{t("hero.exploreButton")}</a>
          </Button>
        </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
